import { Injectable, NgZone } from '@angular/core';
import { ILiveService } from "@live/interfaces/i-live-service";
import { LayoutState, ViewStateRecordsAlias } from "@common/services/layout-state";
import { DataStore } from "@suite/common/data-models/data-store/data-store";
import { ServiceResponse } from "@common/data-models/service/service-response";
import { AppService } from "@suite/app.service";
import { SearchResults } from "@suite/common/data-models/search-results/search-results";
import { SingleViewStateRecord } from "@common/data-models/records/single-viewstate-record";
import { Subscription } from "rxjs";
import { LiveSearchService } from "@live/services/live-search-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { NavigationService } from "@common/services/navigation-service";
import { ObjectTypes } from "@common/enums/object-types";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { ViewStateTypes } from "@common/enums/viewstate-types";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";
import { LanguageSettings } from "@common/services/language-settings";
import { ViewStateOptions } from "@common/data-models/navigation/viewstate-options";
import { ViewStateLayouts } from "@common/enums/viewstate-layouts";
import { FilterDef } from "@common/data-models/navigation/filter-def";
import { LiveSearchResults } from "@live/data-models/live-search-results/live-search-results";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { RundownListViewStateRecord } from "@common/data-models/records/rundown-list-viewstate-record";
import { FieldDef } from "@common/data-models/service/field-def";
import { GridData } from '@suite/common/data-models/misc/grid-data';
import { RundownService } from '@suite/live/services/rundown-service';
import { StoryRecord } from "@live/data-models/records/story-record";
import { DateTime } from "@suite/common/data-models/complex-types/date-time";
import { LookupList } from "@suite/common/data-models/complex-types/lookup-list";
import { LookupListSerializer, SpecialSerializer, ChecklistSerializer, StaffIDListSerializer, AddressSerializer } from "@suite/common/data-models/serializers/serializers";
import { Table } from "@suite/common/data-models/complex-types/table";
import { Address } from "@suite/common/data-models/complex-types/address";
import { MultiValue } from "@suite/common/data-models/complex-types/multi-value";
import { RundownRecord } from "@live/data-models/records/rundown-record";

@Injectable({ providedIn: 'root' })
export class LiveService implements ILiveService {

    private viewStateRecordSubscription: Subscription;
    private viewStateRecordPropertySubscription: Subscription;

    constructor(
        public appService: AppService,
        public liveSearchService: LiveSearchService,
        public viewStateService: LayoutState,
        public visualStateService: VisualStateService,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public nomWebServicesJson: NomWebServicesJson,
        public ngZone: NgZone,
        public navigationService: NavigationService,
        public rundownService: RundownService
    ) {
        LiveSearchResults.liveService = this;
        GridData.liveService = this;
        
        //Clipboard.copyViewToClipboardHandlers["playbook"] = this.copyViewToClipBoard.bind(this);
        //Clipboard.renderers["playbook"] = this.copyToClipboardRenderer.bind(this);

        this.globalTablesService.staticPropertyHandlers["RUNDOWN"] = this.getRundownStaticProperties.bind(this);

        //this.globalTablesService.isPropertyForType[ObjectTypes.WIRE] = this.isTopicProperty.bind(this);
        this.globalTablesService.isPropertyForType[ObjectTypes.SCRIPT] = this.isStoryProperty.bind(this);

        //this.globalTablesService.populateDefaultValueCallbacks[ObjectTypes.WIRE] = this.populateDefaultValuesForTopic.bind(this);

        //this.globalTablesService.setFieldDefCallbacks[ObjectTypes.WIRE] = this.setFieldDefsForTopics.bind(this);

        this.globalTablesService.getGroupingOptions[ViewStateTypes.ROLIST] = this.getGroupingOptionsForRundowns.bind(this);
        //this.globalTablesService.getGroupingOptions[ViewStateTypes.WIRESEARCH] = this.getGroupingOptionsForSearch.bind(this);

        this.globalTablesService.getSortingOptions[ViewStateTypes.ROLIST] = this.getSortingOptionsForRundowns.bind(this);
        //this.globalTablesService.getSortingOptions[ViewStateTypes.WIRESEARCH] = this.getSortingOptionsForSearch.bind(this);

        this.globalTablesService.getLayoutOptions[ViewStateTypes.ROLIST] = this.getLayoutOptionsForRundowns.bind(this);
        //this.globalTablesService.getLayoutOptions[ViewStateTypes.WIRESEARCH] = this.getLayoutOptionsForTopic.bind(this);

        this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.ROLIST] = this.getFiltersForRundowns(ViewStateTypes.ROLIST).bind(this);
        //this.globalTablesService.getFiltersForViewStateType[ViewStateTypes.WIRESEARCH] = this.getFiltersForSearch.bind(this);
    }

    public isStoryProperty(fieldDef: FieldDef): boolean {
        return (
            (fieldDef.Type.indexOf("Script") >= 0) ||
            (fieldDef.Type.indexOf("RO Column") >= 0) ||
            (fieldDef.Type.indexOf("RO+NG Column") >= 0)
        );
    }

    public getRundownStaticProperties(): { [id: string]: boolean } {
        let ret: { [id: string]: boolean } = {};
        return ret;
    }

    public getSortingOptionsForRundowns(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTModTime;
        ret.Options = [
            { id: PSTFieldNames.PSTModTime, title: this.languageSettings.PSTWebGroupByDateLabel }
        ];
        return ret;
    }

    private _groupByRundown: Array<{ id: string, title: string }> = [];
    public get groupByRundown(): Array<{ id: string, title: string }> {
        if ((this._groupByRundown.length == 0) && this.languageSettings) {
            this._groupByRundown = this.getGroupingOptionsForRundowns().Options;
        }
        return this._groupByRundown;
    }

    private _groupByRundownKeys: Array<string> = [];
    public get groupByRundownKeys(): Array<string> {
        if ((this._groupByRundownKeys.length == 0) && (this.groupByRundown.length > 0)) {
            this._groupByRundownKeys = this.groupByRundown.map(item => item.id);
        }
        return this._groupByRundownKeys;
    }



    public getGroupingOptionsForRundowns(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = PSTFieldNames.PSTModTime;
        ret.Options = [
            { id: PSTFieldNames.PSTModTime, title: this.languageSettings.PSTWebGroupByDateLabel }
        ];
        return ret;
    }

    public getLayoutOptionsForRundowns(): ViewStateOptions {
        let ret: ViewStateOptions = new ViewStateOptions();
        ret.Default = ViewStateLayouts.CARD;
        ret.Options = [
            { id: ViewStateLayouts.CARD, title: this.languageSettings.PSTWebViewListLabel }

        ];
        return ret;
    }

    public getFiltersForRundowns(type: number): (item: FieldDef) => boolean {
        return (item: FieldDef) => {
            let type: string = item.Type.toUpperCase();
            if (item.Briefable && (type.indexOf("RO PROPERTY") > -1 || type.indexOf("RO+NG PROPERTY") > -1)) {
                return true;
            }
            return false;
        }
    }

    public activeCollectionPollerStarted: boolean = false;

    public get liveSearchResults(): LiveSearchResults {
        return DataStore.getSearchResults<LiveSearchResults>("live");
    }

    public async loadRundowns(vs?: ViewStateRecordsAlias, guid?: string) {
        if (!vs) vs = this.viewStateService.ViewStateRecord;
        if (this.navigationService.currentListView) this.navigationService.currentListView.showLoader();
        await this.appService.ngZone.runOutsideAngular(async () => {
            //this.appService.LoadingTopicsSource.next(true);
            DataStore.disposeSearchResults();
            SearchResults.newlyAddedItems.dispose();

            let res: ServiceResponse<SearchResults> = await this.liveSearchService.getSearchResults(vs, guid);

            if (!this.activeCollectionPollerStarted) {
                this.activeCollectionPollerStarted = true;
                this.startActiveCollectionPoller();
            }

            if (res.status) {
                DataStore.registerSearchResults("live", res.data);
                this.liveSearchResults.rebuildGroups((<SingleViewStateRecord>vs).viewStateGrouping.Value, (<SingleViewStateRecord>vs).viewStateSorting.Value, (<SingleViewStateRecord>vs).viewStateSortOrder, (<SingleViewStateRecord>vs).viewStateGroupSortOrder);
            } else {
                // Error
            }
            this.appService.LoadingDataSource.next(false);
            this.appService.timeoutQueue.setTimeout(async () => {
                this.appService.ngZone.run(() => {
                    this.navigationService.currentListChangeDetectorRef.detectChanges();
                });
                if (!this.navigationService.CurrentNavigationItem || !this.navigationService.CurrentNavigationItem.isNewView()) {
                    this.navigationService.currentListView.hideLoader();
                }

                this.appService.ngZone.run(() => {
                    this.navigationService.currentListChangeDetectorRef.detectChanges();
                });

                /*if (this.viewStateService.inSlideshowMode) {
                    this.appService.timeoutQueue.setTimeout(() => {
                        this.slideshowService.process();
                    }, 0);
                }*/

                if (this.navigationService.CurrentNavigationItem && this.navigationService.CurrentNavigationItem.isNewView()) {
                    await this.navigationService.saveCurrentNavigationItem();
                    this.navigationService.currentListView.hideLoaderDirectly();
                }
            }, 0);

        });
    }

    public async createStory(database: string, path: string, parentGuid: string, storyRecord: StoryRecord, storyText: string, referalId: string): Promise<ServiceResponse<RundownRecord>> {
        let dataToSend = {};
        for (let p of storyRecord.keys) {
            let value = storyRecord.get(p)
            if (value instanceof DateTime) {
                if (value.get()) dataToSend[p] = value.get();
            } else if (value instanceof LookupList) {
                dataToSend[p] = LookupListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (value instanceof Address) {
                dataToSend[p] = AddressSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (value instanceof MultiValue) {
                if (this.globalTablesService.isStaffIDListFieldType(p)) {
                    dataToSend[p] = StaffIDListSerializer.Instance(this.globalTablesService).serialize(value, p);
                } else {
                    dataToSend[p] = ChecklistSerializer.Instance.serialize(value);
                }
            } else if (value instanceof Table) {
                dataToSend[p] = SpecialSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (typeof value == 'boolean') {
                if (value) {
                    dataToSend[p] = true;
                } else {
                    dataToSend[p] = false;
                }
            } else if (storyRecord.get(p) !== undefined) {
                dataToSend[p] = storyRecord.get(p);
            }
        }
        dataToSend["Text"] = storyText;
        let res = await this.rundownService.createStory(database, path, parentGuid, dataToSend, referalId);
        return res;
    }

    public startActiveCollectionPoller() {
        this.ngZone.runOutsideAngular(async () => {
            setInterval(async () => {
                if (!this.nomWebServicesJson.noSession) {
                    let vs: ViewStateRecordsAlias = this.viewStateService.ViewStateRecord;
                    if (vs instanceof SingleViewStateRecord) {
                        let res = null;
                        //let res = await this.discoverSearchService.getFilteredResultsGuids(vs);
                        if (res && res.status) {
                            //DataStore.getSearchResults('discover').updateNewGuids(res.data, SearchResults.newlyAddedItems.itemsGuids);
                        } else {
                            this.nomWebServicesJson.noSession = true;
                        }
                    }
                }
            }, this.appService.getSolrPollInvervalInSeconds());
        });
    }

    private mosDevicesCache: { [id: string]: any } = {};
    public getMosDeviceByID(mosID: string) {
        if (this.mosDevicesCache[mosID]) {
            return this.mosDevicesCache[mosID];
        }
        for (let mos of this.globalTablesService.MOSDevices) {
            if (mos.ID == mosID) {
                this.mosDevicesCache[mos.ID] = mos;
                return this.mosDevicesCache[mos.ID]
            }
        }

        return null;
    }
}
